import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Reply, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import {
  adminGetGallerySelections, adminGetGalleryComments, adminListGalleryImages,
  adminResolveComment, adminDeleteComment, adminReplyComment, apiErrorMessage,
} from '@/lib/api';
import { ADMIN_PROOFING } from '@/constants/testIds';
import { dialogClass } from './styles';

const ImageThumb = ({ image, size }) => (
  <div className={`${size} flex-shrink-0 rounded-md overflow-hidden border border-[hsl(var(--lxra-border))]`}>
    {image ? <img src={image.image_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-[#141416]" />}
  </div>
);

const SelectionsTab = ({ selections, imagesById }) => {
  const clients = selections?.clients || [];
  if (clients.length === 0) {
    return (
      <p data-testid={ADMIN_PROOFING.selectionsEmpty} className="text-sm text-[hsl(var(--lxra-muted))] text-center py-8">
        No final picks yet. The client hasn't selected any photos.
      </p>
    );
  }
  return (
    <div className="space-y-5">
      {clients.map((c) => (
        <div key={c.client_email}>
          <p className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))] mb-2">
            {c.client_email} — {c.image_ids.length} pick{c.image_ids.length === 1 ? '' : 's'}
          </p>
          <div className="grid grid-cols-4 gap-2">
            {c.image_ids.map((imgId) => <ImageThumb key={imgId} image={imagesById[imgId]} size="aspect-square rounded-lg" />)}
          </div>
        </div>
      ))}
    </div>
  );
};

const CommentRow = ({ comment: c, image, replying, replyDraft, onReplyDraft, replySending, onStartReply, onCancelReply, onSendReply, onResolve, onDelete }) => (
  <div data-testid={ADMIN_PROOFING.commentRow(c.id)} className="flex gap-3 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416] p-3">
    <ImageThumb image={image} size="h-16 w-16" />
    <div className="flex-1 min-w-0">
      <p className="text-xs text-[hsl(var(--lxra-muted))]">{c.client_email}</p>
      <p className="text-sm text-[hsl(var(--lxra-fg))] mt-0.5">{c.body}</p>
      <div className="mt-1.5 flex items-center gap-2">
        {c.is_revision_request && (
          <Badge variant="outline" className="border-amber-400/40 text-amber-400 gap-1 text-[10px]">
            <AlertCircle className="h-3 w-3" /> Revision requested
          </Badge>
        )}
        {c.resolved && (
          <Badge variant="outline" className="border-emerald-400/40 text-emerald-400 gap-1 text-[10px]">
            <CheckCircle2 className="h-3 w-3" /> Resolved
          </Badge>
        )}
      </div>

      {c.photographer_reply && !replying && (
        <div data-testid={ADMIN_PROOFING.replyText(c.id)} className="mt-2.5 rounded-md border-l-2 border-[hsl(var(--lxra-gold))] bg-[#0F0F10] px-3 py-2">
          <p className="text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-gold))] flex items-center gap-1">
            <Reply className="h-3 w-3" /> Your reply
            {c.replied_at && <span className="text-[hsl(var(--lxra-muted))] normal-case tracking-normal">· {new Date(c.replied_at).toLocaleString()}</span>}
          </p>
          <p className="text-sm text-[hsl(var(--lxra-fg))] mt-0.5 whitespace-pre-wrap">{c.photographer_reply}</p>
        </div>
      )}

      {replying && (
        <div className="mt-2.5 space-y-2">
          <Textarea
            autoFocus
            value={replyDraft}
            onChange={(e) => onReplyDraft(e.target.value)}
            placeholder="Reply to the client… (they'll see this in their gallery and get an email if enabled)"
            data-testid={ADMIN_PROOFING.replyTextarea(c.id)}
            className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] text-sm min-h-[72px] resize-none focus-visible:ring-[hsl(var(--lxra-gold))]"
          />
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onCancelReply} disabled={replySending} data-testid={ADMIN_PROOFING.replyCancel(c.id)} className="h-8 text-xs text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-fg))]">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={onSendReply}
              disabled={replySending || !replyDraft.trim()}
              data-testid={ADMIN_PROOFING.replySubmit(c.id)}
              className="h-8 px-3 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.08em] font-semibold disabled:opacity-50"
            >
              {replySending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send Reply</>}
            </Button>
          </div>
        </div>
      )}
    </div>
    <div className="flex flex-col gap-1">
      <Button size="icon" variant="ghost" onClick={onStartReply} data-testid={ADMIN_PROOFING.replyButton(c.id)} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10" title={c.photographer_reply ? 'Edit reply' : 'Reply to client'}>
        <Reply className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" onClick={onResolve} data-testid={ADMIN_PROOFING.resolveButton(c.id)} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-emerald-400 hover:bg-emerald-400/10" title={c.resolved ? 'Mark unresolved' : 'Mark resolved'}>
        <CheckCircle2 className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="ghost" onClick={onDelete} data-testid={ADMIN_PROOFING.deleteButton(c.id)} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10" title="Delete comment">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

/**
 * Client proofing for a private gallery: final picks per client and the
 * comment / revision-request thread with photographer replies.
 */
export const ProofingDialog = ({ open, onOpenChange, slug }) => {
  const [loading, setLoading] = useState(false);
  const [selections, setSelections] = useState(null);
  const [comments, setComments] = useState(null);
  const [imagesById, setImagesById] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replySending, setReplySending] = useState(false);

  useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    setLoading(true);
    setReplyingTo(null);
    setReplyDraft('');
    Promise.all([adminGetGallerySelections(slug), adminGetGalleryComments(slug), adminListGalleryImages(slug)])
      .then(([sel, com, imgs]) => {
        if (cancelled) return;
        setSelections(sel);
        setComments(com);
        setImagesById(Object.fromEntries(imgs.map((i) => [i.id, i])));
      })
      .catch(() => {
        if (cancelled) return;
        setSelections({ clients: [] });
        setComments({ by_image: {} });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, slug]);

  const allComments = useMemo(
    () => (comments ? Object.values(comments.by_image || {}).flat() : []).slice().sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [comments],
  );
  const totalPicks = useMemo(() => (selections?.clients || []).reduce((n, c) => n + c.image_ids.length, 0), [selections]);

  const replaceComment = (comment, updater) =>
    setComments((prev) => {
      const byImage = { ...(prev?.by_image || {}) };
      byImage[comment.image_id] = updater(byImage[comment.image_id] || []);
      return { ...prev, by_image: byImage };
    });

  const handleResolve = async (comment) => {
    try {
      const updated = await adminResolveComment(comment.id, !comment.resolved);
      replaceComment(comment, (list) => list.map((c) => (c.id === comment.id ? updated : c)));
    } catch (err) {
      toast.error('Could not update the comment.');
    }
  };

  const handleDelete = async (comment) => {
    try {
      await adminDeleteComment(comment.id);
      replaceComment(comment, (list) => list.filter((c) => c.id !== comment.id));
    } catch (err) {
      toast.error('Could not delete the comment.');
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
    setReplyDraft('');
  };

  const handleSendReply = async (comment) => {
    const text = replyDraft.trim();
    if (!text) return;
    setReplySending(true);
    try {
      const updated = await adminReplyComment(comment.id, text, true);
      replaceComment(comment, (list) => list.map((c) => (c.id === comment.id ? updated : c)));
      toast.success(updated.reply_email_status === 'sent' ? 'Reply saved and emailed to the client.' : 'Reply saved. The client will see it in their gallery.');
      cancelReply();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send the reply.'));
    } finally {
      setReplySending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={ADMIN_PROOFING.dialog} className={`${dialogClass} max-w-2xl`}>
        <DialogHeader>
          <DialogTitle className="text-[hsl(var(--lxra-fg))]">Client Proofing — {slug}</DialogTitle>
        </DialogHeader>
        {loading && <Skeleton className="h-40 rounded-xl bg-[#141416]" />}
        {!loading && (
          <Tabs defaultValue="selections">
            <TabsList className="bg-[#141416] border border-[hsl(var(--lxra-border))]">
              <TabsTrigger value="selections" data-testid={ADMIN_PROOFING.tabSelections}>
                Final Picks {totalPicks ? `(${totalPicks})` : ''}
              </TabsTrigger>
              <TabsTrigger value="comments" data-testid={ADMIN_PROOFING.tabComments}>
                Comments {allComments.length ? `(${allComments.length})` : ''}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="selections" className="pt-4">
              <SelectionsTab selections={selections} imagesById={imagesById} />
            </TabsContent>

            <TabsContent value="comments" className="pt-4">
              {allComments.length === 0 ? (
                <p data-testid={ADMIN_PROOFING.commentsEmpty} className="text-sm text-[hsl(var(--lxra-muted))] text-center py-8">
                  No comments or revision requests yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {allComments.map((c) => (
                    <CommentRow
                      key={c.id}
                      comment={c}
                      image={imagesById[c.image_id]}
                      replying={replyingTo === c.id}
                      replyDraft={replyDraft}
                      onReplyDraft={setReplyDraft}
                      replySending={replySending}
                      onStartReply={() => { setReplyingTo(c.id); setReplyDraft(c.photographer_reply || ''); }}
                      onCancelReply={cancelReply}
                      onSendReply={() => handleSendReply(c)}
                      onResolve={() => handleResolve(c)}
                      onDelete={() => handleDelete(c)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
